import React from "react";
import { Box, Card, CardContent, Container, Typography } from "@mui/material";
import theme from "../../../theme/Theme";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const EarningsExample = () => {
  return (
    <Box sx={{ ...styles.section, bgcolor: theme.custom.primaryLighter }}>
      <Container maxWidth="lg">
        <Typography sx={styles.sectionTitle}>
          Real Earnings <span style={styles.titleHighLight}>Example</span>
        </Typography>
        <Box sx={styles.earningsContainer}>
          <Typography sx={styles.earningsText}>
            You share your referral link and 10 people join using it. Each of
            those 10 people refers 5 more = 50 second-level referrals. Let's
            break it down level by level
          </Typography>

          <Box sx={styles.earningsBreakdown}>
            {levels.map((level, index) => (
              <Card key={index} sx={styles.levelCard}>
                <CardContent>
                  <Typography sx={styles.levelTitle}>{level.title}</Typography>
                  <Box sx={styles.levelDetails}>
                    <Typography sx={styles.levelDetail}>
                      Commission: <strong>{level.commission}</strong>
                    </Typography>
                    <Typography sx={styles.levelDetail}>
                      Calculation: {level.calculation}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Typography sx={styles.totalEarningsTitle}>
            Total Earnings So Far (Across 5 Levels):
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={levels.map((level, index) => ({
                name: `Level ${index + 1}`,
                earnings: parseFloat(level.earnings),
              }))}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis dataKey="earnings" />
              <Tooltip formatter={(value) => `$${value}`} />
              <Bar
                dataKey="earnings"
                fill={theme.palette.primary.main}
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>

          <Box sx={styles.totalEarnings}>
            <Typography sx={styles.totalAmount}>
              Total: $985 in USDT — earned without buying a single ticket.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const levels = [
  {
    title: "Level 1 – Direct Referrals (10 people)",
    commission: "10%",
    calculation: "10 people x $10 x 10% = $10",
    earnings: "10",
  },
  {
    title: "Level 2 – Indirect Referrals (50 people)",
    commission: "5%",
    calculation: "50 people x $10 x 5% = $25",
    earnings: "25",
  },
  {
    title: "Level 3 – If those 50 refer 5 each (250 people)",
    commission: "3%",
    calculation: "250 x $10 x 3% = $75",
    earnings: "75",
  },
  {
    title: "Level 4 – Those 250 refer 5 each (1,250 people)",
    commission: "2%",
    calculation: "1250 x $10 x 2% = $250",
    earnings: "250",
  },
  {
    title: "Level 5 – Those 1,250 refer 5 each (6,250 people)",
    commission: "1%",
    calculation: "6250 x $10 x 1% = $625",
    earnings: "625",
  },
];

const styles = {
  section: {
    padding: { xs: "60px 0", md: "100px 0" },
    position: "relative",
  },
  sectionTitle: {
    fontWeight: "800",
    margin: "0.2em 2em 0.3em 1em",
    fontSize: { xs: "5.5vw", lg: "3.2vw" },
    textAlign: "center",
    color: theme.custom.black,
    textTransform: "uppercase",
    zIndex: 99,
  },
  titleHighLight: {
    color: theme.palette.primary.main,
    borderBottom: "1.5px solid rgba(0, 0, 0, 1)",
  },
  earningsContainer: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  earningsText: {
    fontSize: "1.2rem",
    textAlign: "center",
    marginBottom: "40px",
    fontFamily: '"Proxima Nova", sans-serif',
  },
  earningsBreakdown: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  levelCard: {
    backgroundColor: theme.custom.white,
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  levelTitle: {
    color: theme.palette.primary.main,
    marginBottom: "10px",
    fontSize: "1.2rem",
    fontWeight: 600,
    fontFamily: '"Proxima Nova", sans-serif',
  },
  levelDetails: {
    display: "flex",
    justifyContent: "space-between",
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    fontFamily: '"Proxima Nova", sans-serif',
  },
  totalEarnings: {
    marginTop: "40px",
    textAlign: "center",
  },
  totalEarningsTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "20px",
    fontFamily: '"Proxima Nova", sans-serif',
    marginTop: "40px",
  },
  totalAmount: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontFamily: '"Proxima Nova", sans-serif',
  },
};

export default EarningsExample;
